import { motion } from "framer-motion";

/**
 * Landing Pages background — a miniature high-converting marketing page
 * with hero typography, social proof, conversion metrics, and email capture.
 */

const avatarColors = [
  "hsl(239 84% 67% / 0.5)",
  "hsl(330 70% 55% / 0.5)",
  "hsl(150 60% 50% / 0.5)",
  "hsl(45 90% 60% / 0.5)",
  "hsl(200 70% 55% / 0.5)",
];

export const ConversionFunnelBg = () => (
  <div className="absolute inset-0 overflow-hidden">
    {/* Gradient mesh background */}
    <motion.div
      className="absolute inset-0"
      style={{
        background:
          "radial-gradient(ellipse 80% 60% at 30% 20%, hsl(239 84% 67% / 0.12) 0%, transparent 70%), " +
          "radial-gradient(ellipse 60% 50% at 75% 70%, hsl(330 70% 55% / 0.08) 0%, transparent 70%)",
      }}
      animate={{
        background: [
          "radial-gradient(ellipse 80% 60% at 30% 20%, hsl(239 84% 67% / 0.12) 0%, transparent 70%), radial-gradient(ellipse 60% 50% at 75% 70%, hsl(330 70% 55% / 0.08) 0%, transparent 70%)",
          "radial-gradient(ellipse 80% 60% at 50% 30%, hsl(280 70% 55% / 0.12) 0%, transparent 70%), radial-gradient(ellipse 60% 50% at 25% 60%, hsl(239 84% 67% / 0.08) 0%, transparent 70%)",
          "radial-gradient(ellipse 80% 60% at 30% 20%, hsl(239 84% 67% / 0.12) 0%, transparent 70%), radial-gradient(ellipse 60% 50% at 75% 70%, hsl(330 70% 55% / 0.08) 0%, transparent 70%)",
        ],
      }}
      transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
    />

    <div className="absolute inset-0 flex flex-col items-center px-5 pt-5 pb-4">
      {/* ── Navbar ── */}
      <div className="w-full flex items-center justify-between mb-4">
        <div className="flex items-center gap-[4px]">
          <div className="w-[8px] h-[8px] rounded-[2px] bg-indigo-400/40" />
          <div className="h-[4px] w-[20px] rounded-full bg-white/20" />
        </div>
        <div className="flex gap-[8px]">
          {[0.6, 0.5, 0.7].map((w, i) => (
            <div key={i} className="h-[3px] rounded-full bg-white/10" style={{ width: `${w * 18}px` }} />
          ))}
        </div>
        <div className="h-[10px] px-[6px] rounded-full bg-indigo-500/30 flex items-center">
          <span className="text-[4px] font-bold text-white/50">CTA</span>
        </div>
      </div>

      {/* ── Hero section ── */}
      <div className="flex flex-col items-center gap-[5px] mb-3 mt-1">
        {/* Big headline */}
        <motion.div
          className="h-[7px] rounded-full bg-white/25"
          initial={{ width: 0 }}
          animate={{ width: "70%" }}
          transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
          style={{ maxWidth: 120 }}
        />
        <motion.div
          className="h-[7px] rounded-full bg-white/15"
          initial={{ width: 0 }}
          animate={{ width: "50%" }}
          transition={{ delay: 0.5, duration: 0.8, ease: "easeOut" }}
          style={{ maxWidth: 90 }}
        />
        {/* Subheadline */}
        <motion.div
          className="h-[3px] rounded-full bg-white/8 mt-[2px]"
          initial={{ width: 0 }}
          animate={{ width: "60%" }}
          transition={{ delay: 0.7, duration: 0.6, ease: "easeOut" }}
          style={{ maxWidth: 100 }}
        />
      </div>

      {/* ── Email capture form ── */}
      <motion.div
        className="flex items-center gap-[3px] mb-3"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.5 }}
      >
        <div className="h-[12px] w-[65px] rounded-md border border-white/[0.1] bg-white/[0.04] flex items-center px-[5px]">
          <motion.div
            className="h-[3px] rounded-full bg-white/15"
            animate={{ width: ["0%", "60%", "60%", "0%"] }}
            transition={{ duration: 5, repeat: Infinity, repeatDelay: 2 }}
          />
        </div>
        <motion.div
          className="h-[12px] px-[8px] rounded-md flex items-center"
          style={{ backgroundColor: "hsl(239 84% 67% / 0.5)" }}
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
        >
          <span className="text-[4.5px] font-bold text-white/70">Get Started</span>
        </motion.div>
      </motion.div>

      {/* ── Social proof row ── */}
      <motion.div
        className="flex items-center gap-[3px] mb-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3, duration: 0.5 }}
      >
        {/* Stacked avatars */}
        <div className="flex -space-x-[3px]">
          {avatarColors.map((color, i) => (
            <motion.div
              key={i}
              className="w-[8px] h-[8px] rounded-full border border-black/30"
              style={{ backgroundColor: color, zIndex: 5 - i }}
              initial={{ opacity: 0, x: -4 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.4 + i * 0.08, duration: 0.3 }}
            />
          ))}
        </div>
        <div className="flex gap-[1px] ml-[3px]">
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="w-[3px] h-[3px] rounded-[0.5px]" style={{ backgroundColor: "hsl(45 90% 60% / 0.6)" }} />
          ))}
        </div>
        <span className="text-[4px] text-white/25 ml-[2px]">2,847 users</span>
      </motion.div>

      {/* ── Conversion metrics floating ── */}
      <div className="w-full flex gap-[5px] mb-2">
        {[
          { label: "Visitors", value: "12.4K", color: "hsl(239 84% 67%)", trend: "+24%" },
          { label: "Conv. Rate", value: "5.8%", color: "hsl(150 60% 50%)", trend: "+1.2%" },
          { label: "Revenue", value: "$48K", color: "hsl(45 90% 60%)", trend: "+18%" },
        ].map((metric, i) => (
          <motion.div
            key={i}
            className="flex-1 rounded-md border border-white/[0.07] p-[5px] flex flex-col gap-[2px]"
            style={{ backgroundColor: "hsl(0 0% 100% / 0.03)" }}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.6 + i * 0.15, duration: 0.5 }}
          >
            <span className="text-[3.5px] text-white/20">{metric.label}</span>
            <div className="flex items-end justify-between">
              <span className="text-[7px] font-mono font-bold" style={{ color: `${metric.color} / 0.7)` }}>
                {metric.value}
              </span>
              <span className="text-[4px] font-mono text-emerald-400/60">{metric.trend}</span>
            </div>
          </motion.div>
        ))}
      </div>

      {/* ── Scroll sections teaser ── */}
      <div className="w-full flex flex-col gap-[4px] mt-auto">
        {[0.85, 0.7].map((w, i) => (
          <motion.div
            key={i}
            className="rounded-md border border-white/[0.05] p-[4px] flex items-center gap-[5px]"
            style={{ backgroundColor: "hsl(0 0% 100% / 0.02)", width: `${w * 100}%` }}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 0.6, x: 0 }}
            transition={{ delay: 2 + i * 0.2, duration: 0.5 }}
          >
            <div className="w-[10px] h-[10px] rounded-[2px] shrink-0" style={{ backgroundColor: `hsl(${239 + i * 50} 70% 60% / 0.15)` }} />
            <div className="flex flex-col gap-[2px] flex-1">
              <div className="h-[3px] rounded-full bg-white/10" style={{ width: `${w * 70}%` }} />
              <div className="h-[2px] rounded-full bg-white/5" style={{ width: `${w * 50}%` }} />
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </div>
);
